/**
 * Registra o service worker em produção e captura o evento
 * `beforeinstallprompt` para permitir mostrar um botão "Instalar app".
 *
 * Uso:
 *   import { registerSW, useInstallPrompt } from "@/lib/pwa";
 *   registerSW();
 *
 * Em qualquer componente:
 *   const { canInstall, install } = useInstallPrompt();
 */
import { useEffect, useState } from "react";

let deferredPrompt = null;
const listeners = new Set();

function emit() {
  for (const cb of listeners) {
    try { cb(!!deferredPrompt); } catch (err) { console.warn("subscribeCanInstall cb:", err); }
  }
}

if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", (e) => {
    e.preventDefault();
    deferredPrompt = e;
    emit();
  });
  window.addEventListener("appinstalled", () => {
    deferredPrompt = null;
    emit();
  });
}

export function registerSW() {
  if (typeof window === "undefined") return;
  if (!("serviceWorker" in navigator)) return;
  // Em desenvolvimento (webpack dev server), o SW pode conflitar com HMR.
  // Só registra quando não estivermos no localhost:3000 direto.
  const isDevServer = window.location.hostname === "localhost" && window.location.port === "3000";
  if (isDevServer) return;
  window.addEventListener("load", () => {
    navigator.serviceWorker
      .register("/sw.js")
      .catch((err) => { /* falha silenciosa — app segue funcionando normalmente */ console.warn("SW register failed:", err); });
  });
}

export function useInstallPrompt() {
  const [canInstall, setCanInstall] = useState(!!deferredPrompt);

  useEffect(() => {
    const cb = (v) => setCanInstall(v);
    listeners.add(cb);
    return () => listeners.delete(cb);
  }, []);

  const install = async () => {
    if (!deferredPrompt) return false;
    deferredPrompt.prompt();
    const choice = await deferredPrompt.userChoice.catch(() => null);
    deferredPrompt = null;
    emit();
    return choice?.outcome === "accepted";
  };

  return { canInstall, install };
}

/** Detecta se o app já está rodando instalado (standalone). */
export function isStandalone() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    // iOS Safari
    window.navigator.standalone === true
  );
}
